import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import Login from './components/auth/Login';
import Profile from './components/profile/Profile';
import ActivityList from './components/rides/ActivityList';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import LanguageSelector from './components/shared/LanguageSelector';
import { API_BASE_URL } from './config';
import AdminPanel from './components/admin/AdminPanel';
import { useJsApiLoader } from '@react-google-maps/api';

import Trainees from './components/admin/Trainees';
import Trainer from './components/admin/Trainer';
import { User } from './types';
import BlogMyArticles from './components/blog/BlogMyArticles';
import BlogEditor from './components/blog/BlogEditor';
import ClubFeed from './components/feed/ClubFeed';
import AICoach from './components/ai_coach/AICoach';
import ClubsListView from './components/clubs/ClubsListView';
import CreateGroupRide from './components/rides/CreateGroupRide';
import GroupRideDetailView from './components/rides/GroupRideDetailView';
import { Club } from './types';



const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

if (!GOOGLE_MAPS_API_KEY) {
  console.error('REACT_APP_GOOGLE_MAPS_API_KEY is not set in environment variables');
}

// Wrapper component for GroupRideDetailView that extracts route params
function GroupRideDetailRoute({ onBack, isLoaded }: { onBack: () => void; isLoaded: boolean }) {
  const { id } = useParams<{ id: string }>();
  const rideId = id ? parseInt(id, 10) : null;
  if (!rideId) return <Navigate to="/clubs" replace />;
  return <GroupRideDetailView rideId={rideId} onBack={onBack} isLoaded={isLoaded} />;
}

// Wrapper component for BlogEditor that extracts route params
function BlogEditorRoute({ onSave, onCancel }: { onSave: () => void; onCancel: () => void }) {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : undefined;
  return <BlogEditor articleId={articleId} onSave={onSave} onCancel={onCancel} />;
}

// ---- SVG Icons for sidebar ----
const Icons = {
  feed: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="14" height="14" rx="3" />
      <line x1="2" y1="7" x2="16" y2="7" />
      <line x1="7" y1="7" x2="7" y2="16" />
    </svg>
  ),
  aiCoach: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2" />
      <circle cx="9" cy="9" r="4" />
      <circle cx="9" cy="9" r="1.5" />
    </svg>
  ),
  rides: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="13" r="3" />
      <circle cx="13" cy="13" r="3" />
      <path d="M5 13l3-8h4l2 4" />
      <path d="M8 5l5 8" />
    </svg>
  ),
  activities: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 12 5 6 8 10 11 4 14 8 16 6" />
      <line x1="2" y1="15" x2="16" y2="15" />
    </svg>
  ),
  clubs: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="6" r="3" />
      <circle cx="13" cy="7" r="2.5" />
      <path d="M1 16c0-3 2.5-5 6-5s6 2 6 5" />
      <path d="M13 9.5c2 0 4 1.2 4 3.5" />
    </svg>
  ),
  journal: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2h12a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
      <line x1="6" y1="6" x2="12" y2="6" />
      <line x1="6" y1="9" x2="12" y2="9" />
      <line x1="6" y1="12" x2="9" y2="12" />
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="6" r="3.5" />
      <path d="M2 16.5c0-3.5 3-6 7-6s7 2.5 7 6" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
      <path d="M14.5 11a1 1 0 00.2 1.1l.04.04a1.2 1.2 0 11-1.7 1.7l-.04-.04a1 1 0 00-1.1-.2 1 1 0 00-.6.92v.1a1.2 1.2 0 11-2.4 0v-.05a1 1 0 00-.65-.92 1 1 0 00-1.1.2l-.04.04a1.2 1.2 0 11-1.7-1.7l.04-.04a1 1 0 00.2-1.1 1 1 0 00-.92-.6h-.1a1.2 1.2 0 110-2.4h.05a1 1 0 00.92-.65 1 1 0 00-.2-1.1l-.04-.04a1.2 1.2 0 111.7-1.7l.04.04a1 1 0 001.1.2h.05a1 1 0 00.6-.92v-.1a1.2 1.2 0 012.4 0v.05a1 1 0 00.6.92 1 1 0 001.1-.2l.04-.04a1.2 1.2 0 111.7 1.7l-.04.04a1 1 0 00-.2 1.1v.05a1 1 0 00.92.6h.1a1.2 1.2 0 110 2.4h-.05a1 1 0 00-.92.6z" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 16H3.5a1.5 1.5 0 01-1.5-1.5v-11A1.5 1.5 0 013.5 2h3" />
      <polyline points="12 13 16 9 12 5" />
      <line x1="16" y1="9" x2="6" y2="9" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="2" y1="4.5" x2="16" y2="4.5" />
      <line x1="2" y1="9" x2="16" y2="9" />
      <line x1="2" y1="13.5" x2="16" y2="13.5" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="7.5" r="5" />
      <line x1="16" y1="16" x2="11.5" y2="11.5" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="9" y1="3" x2="9" y2="15" />
      <line x1="3" y1="9" x2="15" y2="9" />
    </svg>
  ),
};

// ---- Page title map ----
function usePageTitle(pathname: string, t: (key: string) => string): string {
  return useMemo(() => {
    if (pathname.startsWith('/feed')) return t('navigation.feed');
    if (pathname.startsWith('/ai-coach')) return 'AI Coach';
    if (pathname.startsWith('/clubs')) return t('navigation.clubs');
    if (pathname.startsWith('/rides/create')) return t('navigation.activities');
    if (pathname.startsWith('/rides')) return t('navigation.activities');
    if (pathname.startsWith('/blog')) return t('blog.magazine');
    if (pathname.startsWith('/admin')) return 'Admin';
    if (pathname.startsWith('/profile')) return t('navigation.profile');
    if (pathname.startsWith('/trainees')) return 'Trainees';
    if (pathname.startsWith('/trainer')) return 'Trainer';
    return '';
  }, [pathname, t]);
}

function AppContent() {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
  });
  const { t, setLanguage, syncLanguageFromDatabase } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const loginIntentRef = React.useRef(false);

  // Enter app after explicit login (useRef avoids timing race with async state updates)
  useEffect(() => {
    if (user && loginIntentRef.current) {
      loginIntentRef.current = false;
      navigate('/ai-coach');
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(true);
  const [editingArticleId, setEditingArticleId] = useState<number | undefined>(undefined);
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState('');
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [totalHours, setTotalHours] = useState<number>(0);
  const [totalElevation, setTotalElevation] = useState<number>(0);
  const [userClubs, setUserClubs] = useState<Club[]>([]);
  const [adminBadgeCount, setAdminBadgeCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [insightData, setInsightData] = useState<any>(null);

  // Mobile app banner (must be before any conditional returns - React hooks rule)
  const [showAppBanner, setShowAppBanner] = useState(() => {
    if (localStorage.getItem('hideAppBanner')) return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  });

  const dismissAppBanner = () => {
    setShowAppBanner(false);
    localStorage.setItem('hideAppBanner', '1');
  };

  const pageTitle = usePageTitle(location.pathname, t);
  const isAICoachPage = location.pathname.startsWith('/ai-coach');

  // Persist last path for session restore
  useEffect(() => {
    if (user && location.pathname !== '/') {
      sessionStorage.setItem('lastPath', location.pathname);
    }
  }, [location.pathname, user]);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch AI Coach insights for topbar
  useEffect(() => {
    if (!user || !isAICoachPage) return;
    fetch(`${API_BASE_URL}/api/analytics/profile`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setInsightData(data); })
      .catch(() => {});
  }, [user, isAICoachPage]);

  useEffect(() => {
    // Handle Strava dev callback (tokens from production proxy)
    if (window.location.pathname === '/strava-callback') {
      handleStravaDevCallback();
      return;
    }

    checkAuthStatus();
  }, []);

  const handleStravaDevCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');
    const expiresAt = urlParams.get('expires_at');
    const stravaUserId = urlParams.get('strava_user_id');
    const error = urlParams.get('error');

    if (error) {
      console.error('[App] Strava callback error:', error);
      window.location.href = '/profile?strava=error';
      return;
    }

    if (accessToken && refreshToken && expiresAt && stravaUserId) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/strava-dev-callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: expiresAt,
            strava_user_id: stravaUserId
          })
        });

        if (response.ok) {
          console.log('[App] Strava tokens saved successfully');
          window.location.href = '/profile?strava=success';
        } else {
          console.error('[App] Failed to save Strava tokens');
          window.location.href = '/profile?strava=error';
        }
      } catch (error) {
        console.error('[App] Error saving Strava tokens:', error);
        window.location.href = '/profile?strava=error';
      }
    } else {
      console.error('[App] Missing Strava callback parameters');
      window.location.href = '/profile?strava=error';
    }
  };

  useEffect(() => {
    if (user) {
      fetchTotalStats();
    }
  }, [user]);

  const checkAuthStatus = async () => {
    // Skip auth check if we're on email verification page
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const isEmailVerification = resetToken && window.location.pathname.includes('verify-email');

    if (isEmailVerification) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);

        // Sync language from database
        await syncLanguageFromDatabase();

        // Fetch user's clubs for nav badge and feed switcher
        await fetchUserClubs();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserClubs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUserClubs(data.clubs || []);
        const totalPending = (data.clubs || [])
          .filter((c: Club) => c.role === 'admin')
          .reduce((sum: number, c: Club) => sum + (c.pending_request_count || 0), 0);
        setAdminBadgeCount(totalPending);
      }
    } catch { /* ignore */ }
  };

  const fetchTotalStats = async () => {
    return;
  };

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        loginIntentRef.current = true;
        await checkAuthStatus();
      } else {
        const errorData = await response.json();
        setLoginError(errorData.error || t('auth.loginError'));
      }
    } catch (error) {
      setLoginError(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData: any) => {
    setLoading(true);
    setRegisterError('');
    setRegistrationSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const data = await response.json();
        setRegisterError('');
        setRegistrationSuccess(data.message);
        setTimeout(() => {
          setRegistrationSuccess('');
        }, 8000);
      } else {
        const errorData = await response.json();
        setRegisterError(errorData.error || t('auth.registerError'));
      }
    } catch (error) {
      setRegisterError(t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerification = async (token: string) => {
    setLoading(true);
    setRegisterError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data = await response.json();
        setRegisterError(data.message);
        setTimeout(() => {
          window.location.href = '/?verified=true';
        }, 3000);
      } else {
        const errorData = await response.json();
        setRegisterError(errorData.error || t('emailVerification.emailVerificationFailed'));
      }
    } catch (error) {
      setRegisterError(t('emailVerification.emailVerificationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/logout`, {
        credentials: 'include'
      });
      setUser(null);
      sessionStorage.removeItem('lastPath');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleStravaAuth = async () => {
    console.log('Initiating Strava auth...');
    try {
      window.location.href = `${API_BASE_URL}/auth/strava`;
    } catch (error) {
      console.error('Strava auth failed:', error);
    }
  };

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="app-loading-screen">
        <div className="app-loading-card">
          <h2>{t('common.loading')}</h2>
        </div>
      </div>
    );
  }

  // DevBoard migrated to GitHub Issues -- redirect
  if (window.location.pathname === '/todo' || window.location.pathname.startsWith('/todo/')) {
    window.location.href = 'https://github.com/formit04/alpsepic/issues';
    return null;
  }

  // ---- UNAUTHENTICATED ----
  if (!user) {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const isResetPassword = resetToken && window.location.pathname.includes('reset-password');
    const isEmailVerification = resetToken && window.location.pathname.includes('verify-email');
    const isVerified = urlParams.get('verified') === 'true';

    // Handle email verification page
    if (isEmailVerification) {
      return (
        <div className="app-loading-screen" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/tlo.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div style={{ maxWidth: 480, width: '100%', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <img src={`${process.env.PUBLIC_URL}/alpsepics-logo.png`} alt="AlpsEpics" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            </div>
            <div className="card" style={{ textAlign: 'center', padding: 40, background: 'rgba(255,255,255,0.95)' }}>
              <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>{t('emailVerification.verifyEmail')}</h2>
              <p style={{ color: 'var(--ae-text-secondary)', marginBottom: 24 }}>{t('emailVerification.verifyingEmail')}</p>
              <button
                onClick={() => handleEmailVerification(resetToken!)}
                className="btn"
                disabled={loading}
              >
                {loading ? t('emailVerification.verifyingEmail') : t('emailVerification.verifyEmail')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Show Login
    return (
      <>
        {registrationSuccess && (
          <div className="app-banner app-banner-success">
            <span>&#10003;</span>
            {registrationSuccess}
          </div>
        )}
        {isVerified && (
          <div className="app-banner app-banner-verified">
            {t('emailVerification.emailVerified')}
          </div>
        )}
        {loginError && (
          <div className="app-banner app-banner-error">
            {loginError}
          </div>
        )}
        <Login
          onLogin={handleLogin}
          onRegister={handleRegister}
          loading={loading}
          initialView={isResetPassword ? 'reset-password' : undefined}
          resetToken={resetToken}
        />
      </>
    );
  }

  // ---- AUTHENTICATED — App Shell ----

  const handleEditArticle = (articleId: number) => {
    setEditingArticleId(articleId);
    navigate(`/blog/editor/${articleId}`);
  };

  const handleCreateArticle = () => {
    setEditingArticleId(undefined);
    navigate('/blog/editor');
  };

  const handleBlogEditorSave = () => {
    setEditingArticleId(undefined);
    navigate('/blog/my-articles');
  };

  const handleBlogEditorCancel = () => {
    setEditingArticleId(undefined);
    navigate('/blog/my-articles');
  };

  const userInitials = user ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() : '';

  const navItems = [
    { group: 'Core', items: [
      { path: '/feed', label: t('navigation.feed'), icon: Icons.feed },
      { path: '/ai-coach', label: 'AI Coach', icon: Icons.aiCoach },
      { path: '/clubs', label: t('navigation.clubs'), icon: Icons.clubs, badge: adminBadgeCount > 0 ? adminBadgeCount : undefined },
      { path: '/rides', label: t('navigation.activities'), icon: Icons.activities },
    ]},
    ...(user?.club ? [{ group: 'Club', items: [
      { path: '/blog/my-articles', label: t('blog.magazine'), icon: Icons.journal },
    ]}] : []),
    { group: 'Account', items: [
      { path: '/profile', label: t('navigation.profile'), icon: Icons.profile },
      ...(user?.is_admin ? [{ path: '/admin', label: 'Admin', icon: Icons.admin }] : []),
    ]},
  ];

  return (
    <div className="app-shell">
      {/* Mobile App Banner */}
      {showAppBanner && (
        <div className="mobile-app-banner">
          <img src={`${process.env.PUBLIC_URL}/alpsepics-logo.png`} alt="" className="mobile-app-banner-icon" />
          <div className="mobile-app-banner-text">
            <strong>AlpsEpics</strong>
            <span>{t('common.betterOnApp')}</span>
          </div>
          <button className="mobile-app-banner-btn" onClick={() => {
            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
            const scheme = isIOS ? 'alpsepicai://' : 'alpsepic://';
            const fallback = 'https://alpsepics.com/mobile';
            const start = Date.now();
            window.location.href = scheme;
            setTimeout(() => {
              if (Date.now() - start < 2000) window.location.href = fallback;
            }, 1500);
          }}>
            {t('common.openApp')}
          </button>
          <button className="mobile-app-banner-close" onClick={dismissAppBanner} aria-label="Close">&times;</button>
        </div>
      )}

      {/* Sidebar Overlay (mobile) */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <img src={`${process.env.PUBLIC_URL}/alpsepics-logo.png`} alt="AlpsEpics" />
          <span className="sidebar-logo-text">AlpsEpics</span>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((group) => (
            <div key={group.group} className="sidebar-nav-group">
              <div className="sidebar-nav-group-label">{group.group}</div>
              {group.items.map((item) => {
                const isActive = item.path === '/feed'
                  ? location.pathname === '/feed'
                  : item.path === '/blog/my-articles'
                  ? location.pathname.startsWith('/blog')
                  : location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                  >
                    <span className="sidebar-nav-icon">{item.icon}</span>
                    {item.label}
                    {item.badge && <span className="sidebar-nav-badge">{item.badge}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {user.google_picture
              ? <img src={user.google_picture} alt="" />
              : userInitials
            }
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user.first_name} {user.last_name}</div>
            <div className="sidebar-user-role">{user.is_admin ? 'Admin' : 'Member'}</div>
          </div>
          <button className="sidebar-logout-btn" onClick={handleLogout} title={t('auth.logout')}>
            <span className="sidebar-nav-icon">{Icons.logout}</span>
          </button>
        </div>
      </aside>

      {/* Topbar */}
      <header className="app-topbar">
        <div className="topbar-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
            <span className="sidebar-nav-icon">{Icons.menu}</span>
          </button>
          <span className="topbar-page-title">{pageTitle}</span>
        </div>

        {isAICoachPage && insightData && (
          <div className="topbar-insights">
            <div className="topbar-insight">
              <span className="topbar-insight-label">Load</span>
              <span className="topbar-insight-value">{insightData.training_load ?? '—'}</span>
              {insightData.training_load_trend != null && (
                <span className={`topbar-insight-trend ${insightData.training_load_trend > 0 ? 'up' : insightData.training_load_trend < 0 ? 'down' : 'neutral'}`}>
                  {insightData.training_load_trend > 0 ? '↑' : insightData.training_load_trend < 0 ? '↓' : '→'}{Math.abs(insightData.training_load_trend)}%
                </span>
              )}
            </div>
            <div className="topbar-insight">
              <span className="topbar-insight-label">Fatigue</span>
              <span className="topbar-insight-value">{insightData.fatigue ?? '—'}</span>
              {insightData.fatigue_trend != null && (
                <span className={`topbar-insight-trend ${insightData.fatigue_trend > 0 ? 'up' : insightData.fatigue_trend < 0 ? 'down' : 'neutral'}`}>
                  {insightData.fatigue_trend > 0 ? '↑' : insightData.fatigue_trend < 0 ? '↓' : '→'}{Math.abs(insightData.fatigue_trend)}%
                </span>
              )}
            </div>
            <div className="topbar-insight">
              <span className="topbar-insight-label">Readiness</span>
              <span className="topbar-insight-value">{insightData.readiness ?? '—'}</span>
              {insightData.readiness_trend != null && (
                <span className={`topbar-insight-trend ${insightData.readiness_trend > 0 ? 'up' : insightData.readiness_trend < 0 ? 'down' : 'neutral'}`}>
                  {insightData.readiness_trend > 0 ? '↑' : insightData.readiness_trend < 0 ? '↓' : '→'}{Math.abs(insightData.readiness_trend)}%
                </span>
              )}
            </div>
            <div className="topbar-insight">
              <span className="topbar-insight-label">Next</span>
              <span className="topbar-insight-value">{insightData.next_session ?? '—'}</span>
            </div>
            <button
              className="topbar-analytics-btn"
              onClick={() => navigate('/ai-coach?tab=analytics')}
              title="Extended AI Analytics"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Analytics
            </button>
          </div>
        )}

        <div className="topbar-right">
          <LanguageSelector />
          <button
            className="topbar-action-btn"
            onClick={() => navigate('/rides/create')}
          >
            <span className="sidebar-nav-icon">{Icons.plus}</span>
            <span>New Ride</span>
          </button>
          <div className="topbar-user-avatar" onClick={() => navigate('/profile')}>
            {user.google_picture
              ? <img src={user.google_picture} alt="" />
              : userInitials
            }
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="app-main">
        <main className="app-content">
          <Routes>
            <Route path="/feed" element={
              <ClubFeed
                isLoaded={isLoaded}
                user={user!}
                userClubs={userClubs}
                onViewArticle={(slug) => {
                  window.open(`https://alpsepics.com/blog/${slug}`, '_blank');
                }}
                onViewAll={() => navigate('/blog/my-articles')}
                onNavigateToClubs={() => navigate('/clubs')}
              />
            } />
            <Route path="/profile" element={<Profile user={user} onStravaAuth={handleStravaAuth} />} />
            <Route path="/rides" element={<ActivityList isLoaded={isLoaded} />} />
            <Route path="/rides/create" element={
              <CreateGroupRide
                onBack={() => navigate('/clubs')}
                onRideCreated={(rideId) => navigate(`/rides/${rideId}`)}
              />
            } />
            <Route path="/rides/:id" element={
              <GroupRideDetailRoute onBack={() => navigate('/clubs')} isLoaded={isLoaded} />
            } />
            <Route path="/clubs" element={
              <ClubsListView
                user={user!}
                onClubsChanged={fetchUserClubs}
                onNavigateToClub={(clubId) => {
                  localStorage.setItem('selected_club_id', String(clubId));
                  navigate('/feed');
                }}
                onCreateGroupRide={() => navigate('/rides/create')}
                onSelectRide={(rideId) => navigate(`/rides/${rideId}`)}
              />
            } />
            <Route path="/ai-coach" element={
              <AICoach user={user} />
            } />
            <Route path="/blog/editor/:id" element={
              user?.club ? (
                <BlogEditorRoute onSave={handleBlogEditorSave} onCancel={handleBlogEditorCancel} />
              ) : <p>{t('common.accessDenied')}</p>
            } />
            <Route path="/blog/editor" element={
              user?.club ? (
                <BlogEditor
                  articleId={editingArticleId}
                  onSave={handleBlogEditorSave}
                  onCancel={handleBlogEditorCancel}
                />
              ) : <p>{t('common.accessDenied')}</p>
            } />
            <Route path="/blog/my-articles" element={
              user?.club ? (
                <BlogMyArticles
                  onEdit={handleEditArticle}
                  onCreate={handleCreateArticle}
                  onView={(slug) => {
                    window.open(`https://alpsepics.com/blog/${slug}`, '_blank');
                  }}
                />
              ) : <p>{t('common.accessDenied')}</p>
            } />
            <Route path="/admin" element={
              user && user.is_admin ? <AdminPanel /> : <p>{t('common.accessDenied')}</p>
            } />
            <Route path="/trainees" element={
              user && user.is_trainer ? <Trainees currentUser={user} /> : <p>{t('common.accessDenied')}</p>
            } />
            <Route path="/trainer" element={<Trainer user={user} />} />
            <Route path="*" element={<Navigate to="/ai-coach" replace />} />
          </Routes>
        </main>
        <footer className="app-footer">
          &copy; {new Date().getFullYear()} AlpsEpics. All rights reserved.
          <br />
          Crafted &amp; Designed &amp; Built with <span style={{ color: '#C43838' }}>&hearts;</span> by <a href="https://codezuno.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>codezuno.com</a>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
